import React from 'react';
import { Bug, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ordersAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';

const DiagnosticDialog = ({
  open,
  onOpenChange,
  debugSearchTerm,
  setDebugSearchTerm,
  debugResult,
  setDebugResult,
  debugLoading,
  setDebugLoading,
  summaryData,
  customStartDate,
  customEndDate
}) => {
  const runDiagnostic = async () => {
    if (!debugSearchTerm.trim()) {
      toast.error('Ingresa el nombre del producto a buscar');
      return;
    }
    
    const startDate = summaryData?.start_date || customStartDate || getToday();
    const endDate = summaryData?.end_date || customEndDate || getToday();
    
    setDebugLoading(true);
    try {
      const response = await ordersAPI.debugProductOrders(debugSearchTerm, startDate, endDate);
      setDebugResult(response.data);
    } catch (error) {
      console.error('Error en diagnóstico:', error);
      toast.error('Error al ejecutar diagnóstico');
    } finally {
      setDebugLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-amber-600" />
            Diagnosticar Pedidos de Producto
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Nombre del producto (ej: Nori)"
              value={debugSearchTerm}
              onChange={(e) => setDebugSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="debug-search-input"
            />
            <Button onClick={runDiagnostic} disabled={debugLoading} data-testid="debug-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {debugLoading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
          
          <p className="text-sm text-slate-500">
            Buscará en el período: {summaryData?.start_date || customStartDate || 'hoy'} al {summaryData?.end_date || customEndDate || 'hoy'}
          </p>
          
          {debugResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-bold text-amber-800 mb-2">Resumen para "{debugResult.search_term}"</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">Total (enviados):</span>
                    <span className="font-bold text-green-700 ml-2">{debugResult.summary.total_quantity_submitted_only}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total (todos):</span>
                    <span className="font-bold text-blue-700 ml-2">{debugResult.summary.total_quantity_all_status}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Sin enviar:</span>
                    <span className={`font-bold ml-2 ${debugResult.summary.difference > 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {debugResult.summary.difference}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">Líneas de pedido:</span>
                    <span className="font-bold ml-2">{debugResult.summary.total_order_lines}</span>
                  </div>
                </div>
              </div>
              
              {/* Duplicate detection */}
              {Object.keys(debugResult.by_item_id).length > 1 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-bold text-red-800 mb-2">⚠️ Productos duplicados detectados</h4>
                  <p className="text-sm text-red-700 mb-2">
                    Hay {Object.keys(debugResult.by_item_id).length} productos diferentes con el mismo nombre. 
                    Esto puede causar que las cantidades no se sumen correctamente.
                  </p>
                  <div className="space-y-1 text-sm">
                    {Object.entries(debugResult.by_item_id).map(([id, data]) => (
                      <div key={id} className="flex justify-between">
                        <span>ID: {id} - {data.name}</span>
                        <span className="font-bold">{data.total} ({data.orders} pedidos)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Order details */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <h4 className="font-semibold">Detalle de cada pedido ({debugResult.orders_detail.length})</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Local</th>
                        <th className="px-3 py-2 text-left">Maestro</th>
                        <th className="px-3 py-2 text-right">Cantidad</th>
                        <th className="px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {debugResult.orders_detail.map((order, i) => (
                        <tr key={i} className={order.order_status !== 'submitted' ? 'bg-red-50' : ''}>
                          <td className="px-3 py-2">{order.order_date}</td>
                          <td className="px-3 py-2">{order.location_name}</td>
                          <td className="px-3 py-2 text-xs">{order.maestro || '-'}</td>
                          <td className="px-3 py-2 text-right font-bold">{order.requested_quantity}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              order.order_status === 'submitted' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {order.order_status === 'submitted' ? 'Enviado' : order.order_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiagnosticDialog;
